/**
 * WB4.5 — Domain top-5 handler dispatch coverage SSOT.
 * Used by static gate + jest to ensure no top-5 id falls through to handled:false.
 */

import { SELLABLE_GOLDEN_HANDLER_REFS } from './sellable-action-rule-table';
import { isInteractionSurfaceHandlerRef } from './workbench-interaction-surface-contract';
import {
  isBulkStateChangeHandlerRef,
  isFinanceCompensatingHandlerRef,
  isHighTierHandlerRef,
  requiresPopconfirmGate,
} from './workbench-action-recoverability';
import { isPaletteOperationalFilterHandlerRef } from './workbench-palette-operational-filter';
import {
  COLLECTION_DOMAIN_HANDLER_REFS,
  SUPPLIER_RECEIVING_DOMAIN_HANDLER_REFS,
} from './workbench-domain-action-handler-refs';

export const DOMAIN_DIRECT_KERNEL_HANDLER_REFS = new Set<string>([
  SELLABLE_GOLDEN_HANDLER_REFS.openDetail,
  SELLABLE_GOLDEN_HANDLER_REFS.editDescription,
  SELLABLE_GOLDEN_HANDLER_REFS.viewAudit,
  SELLABLE_GOLDEN_HANDLER_REFS.viewAuditBulk,
  'crud:view',
  'crud:edit',
  'crud:duplicate',
  'crud:restore',
  'navigate:audit',
  'navigate:related',
  'clipboard:copy-id',
  'document:attach_evidence',
  COLLECTION_DOMAIN_HANDLER_REFS.openContactFlow,
  COLLECTION_DOMAIN_HANDLER_REFS.recordCommitment,
  SUPPLIER_RECEIVING_DOMAIN_HANDLER_REFS.openGoodsReceiptFlow,
]);

function isGatedDomainKernelHandlerRef(handlerRef: string): boolean {
  return (
    requiresPopconfirmGate(handlerRef) ||
    isHighTierHandlerRef(handlerRef) ||
    isFinanceCompensatingHandlerRef(handlerRef) ||
    isBulkStateChangeHandlerRef(handlerRef)
  );
}

export function isDomainTop5HandlerDispatchable(handlerRef: string): boolean {
  return (
    DOMAIN_DIRECT_KERNEL_HANDLER_REFS.has(handlerRef) ||
    isGatedDomainKernelHandlerRef(handlerRef) ||
    isInteractionSurfaceHandlerRef(handlerRef)
  );
}

export type DomainHandlerDispatchLane = 'direct' | 'gated' | 'unwired';

export function resolveDomainHandlerDispatchLane(handlerRef: string): DomainHandlerDispatchLane {
  if (DOMAIN_DIRECT_KERNEL_HANDLER_REFS.has(handlerRef)) {
    return 'direct';
  }
  if (isGatedDomainKernelHandlerRef(handlerRef)) {
    return 'gated';
  }
  return 'unwired';
}

/** Blocked at dynamic registry — domain manifests must use wired viewAudit handler instead. */
const DYNAMIC_REGISTRY_BLOCKED_HANDLER_REFS = new Set<string>(['navigate:audit']);

/** Runtime dynamic registry — flow bindings · workspace tasks · intent quick actions only. */
export function isWorkbenchDynamicRegistryHandlerRef(handlerRef: string): boolean {
  return (
    handlerRef.startsWith('flow:start:') ||
    handlerRef.startsWith('task:') ||
    handlerRef.startsWith('workbench:intent:') ||
    handlerRef === 'workbench:export'
  );
}

export function assertRegisterableWorkbenchAction(action: {
  id: string;
  handlerRef: string;
}): void {
  if (DYNAMIC_REGISTRY_BLOCKED_HANDLER_REFS.has(action.handlerRef)) {
    throw new Error(
      `registerWorkbenchActions: blocked noop handlerRef ${action.handlerRef} for action ${action.id}`
    );
  }
  if (!isWorkbenchDynamicRegistryHandlerRef(action.handlerRef)) {
    throw new Error(
      `registerWorkbenchActions: handlerRef ${action.handlerRef} for action ${action.id} must use static rule tables, not dynamic registry`
    );
  }
}

/** Workspace assignment · my-tasks surfaces — dispatched outside ActionExecutor switch. */
export function isManifestWorkspaceSurfaceHandlerRef(handlerRef: string): boolean {
  return handlerRef.startsWith('assignment:') || handlerRef.startsWith('my-tasks:');
}

/** Manifest SSOT — static rule-table handler refs must be direct, gated, or dynamic-registry allowed. */
export function isManifestKernelHandlerRefDispatchable(handlerRef: string): boolean {
  return (
    isDomainTop5HandlerDispatchable(handlerRef) ||
    isWorkbenchDynamicRegistryHandlerRef(handlerRef) ||
    isManifestWorkspaceSurfaceHandlerRef(handlerRef) ||
    isPaletteOperationalFilterHandlerRef(handlerRef)
  );
}
